import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Icon from 'srcReact/components/icons/Icon'
import styles from './styles.module.scss'
import ErrorMessage from '../ErrorMessage'
import { useTranslation } from 'react-i18next'
import { isMobileScreen } from 'srcReact/helpers/utils'

export default function SearchTextBox({
  size,
  icon,
  iconPosition,
  iconCSS = '',
  isIconFlipped,
  placeholder,
  handleSearch,
  handleKeyDown,
  minimumSearchChar,
  errorMessage,
  cssClass,
  inputCssClass,
  buttonCssClass,
  value,
  handleFocus,
  handleBlur,
  type,
  disabled,
  id,
  handleIconClick,
  enterkeyhint,
  clear,
  style = 'default',
  ariaLabel
}) {
  const [minCharMessage, setMinCharMessage] = useState('')
  const [inputValue, setInputValue] = useState(value || '')
  const inputRef = useRef(null)
  const { t } = useTranslation()
  useEffect(() => {
    if (clear === true) {
      setInputValue('')
    }
  }, [clear])
  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleChange = (e) => {
    if (handleSearch) {
      const value = e.target.value
      setInputValue(value)
      if (minimumSearchChar && value.length > 0 && value.length < minimumSearchChar) {
        setMinCharMessage(errorMessage)
        return
      }
      setMinCharMessage('')
      handleSearch(value)
    }
  }

  const handleKeyDownEvent = (e) => {
    setMinCharMessage('')

    if (handleKeyDown) {
      if (e.key === 'Enter') {
        const value = inputRef ? inputRef.current.value : ''
        handleKeyDown(value)
      } else if (!isMobileScreen()) {
        handleKeyDown(e.key)
      }
    }
  }

  const clearText = () => {
    setInputValue('')
    handleSearch('')
  }

  return (
    <div className={`${styles.searchContainer} ${cssClass ? cssClass : ''}`}>
      <span className={`${styles.searchControl} ${style === 'secondary' && styles.secondarySearchControl}`}>
        {iconPosition === 'start' && (
          <button
            tabIndex={inputValue.length > 0 ? 0 : -1}
            aria-label={inputValue.length ? t('formlabel.clearSearch') : t('global.search')}
            type='button'
            className={`${styles.buttonIconContainer} ${
              inputValue.length === 0 && styles.buttonIconContainerCursor
            } ${buttonCssClass}`}
            onClick={handleIconClick ? handleIconClick : inputValue.length > 0 ? clearText : null}>
            <Icon
              styleModifier={iconCSS}
              name={icon ? icon : inputValue.length > 0 ? 'close24' : 'search24'}
              size={size}
              isFlipped={!isIconFlipped}
            />
          </button>
        )}

        <input
          value={inputValue}
          ref={inputRef}
          className={`${styles.input} ${inputCssClass}`}
          type={type}
          enterkeyhint={enterkeyhint}
          id={id}
          disabled={disabled}
          autoComplete='off'
          title={placeholder}
          placeholder={placeholder}
          onChange={(e) => handleChange(e)}
          autoCorrect='off'
          spellCheck='false'
          onKeyDown={(e) => handleKeyDownEvent(e)}
          onFocus={(e) => handleFocus && handleFocus(e)}
          onBlur={(e) => handleBlur && handleBlur(e)}
          aria-label={ariaLabel}
        />
        {iconPosition === 'end' && (
          <button
            aria-label={placeholder}
            type='button'
            className={`${styles.buttonIconContainer} ${
              inputValue.length === 0 && styles.buttonIconContainerCursor
            } ${buttonCssClass}`}
            onClick={handleIconClick ? handleIconClick : inputValue.length > 0 ? clearText : null}>
            <Icon
              name={icon ? icon : inputValue.length > 0 ? 'close24' : 'search24'}
              size={size}
              isFlipped={!isIconFlipped}
            />
          </button>
        )}
      </span>
      {minimumSearchChar && minCharMessage && <ErrorMessage text={minCharMessage} className={styles.error} />}
    </div>
  )
}

SearchTextBox.propTypes = {
  value: PropTypes.string,
  icon: PropTypes.string,
  size: PropTypes.string,
  enterkeyhint: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  iconPosition: PropTypes.string,
  isIconFlipped: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  handleSearch: PropTypes.func.isRequired,
  handleKeyDown: PropTypes.func,
  cssClass: PropTypes.string,
  inputCssClass: PropTypes.string,
  handleFocus: PropTypes.func,
  handleBlur: PropTypes.func,
  handleIconClick: PropTypes.func,
  buttonCssClass: PropTypes.string,
  style: PropTypes.oneOf(['default', 'secondary'])
}

SearchTextBox.defaultProps = {
  value: '',
  icon: '',
  id: '',
  enterkeyhint: 'search',
  type: 'text',
  size: 'large',
  iconPosition: 'start',
  isIconFlipped: true,
  disabled: false,
  placeholder: '',
  cssClass: '',
  inputCssClass: '',
  buttonCssClass: '',
  style: 'default'
}
